import * as Sentry from '@sentry/browser';
import * as Cookies from 'es-cookie';
import { authClient } from 'api';
import { client } from 'api/apiClient';
import { getAuthToken } from 'helpers/cookieUtils';
import Loading from 'loading';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reidentificationTokenKey } from 'types/admin';

/* eslint-disable react/jsx-props-no-spreading */

const clearTokenStorage = () => {
  localStorage.removeItem(reidentificationTokenKey);
};

const getTokenFromStorage = () => getAuthToken();

const clearAuthCookies = () => {
  const cookieNames = [
    'whoop-auth-token',
    'whoop-auth-user',
    'whoop-auth-refresh-token',
    'whoop-auth-expiry',
  ];

  const domains = [
    { domain: '.whoopunite.com', secure: true },
    { domain: '.dev.whoopunite.com', secure: true },
    { domain: '.whoop.com', secure: true },
    { domain: '.dev.whoop.com', secure: true },
    { domain: 'localhost', secure: false },
  ];

  // Remove cookies from all possible domains to ensure cleanup
  cookieNames.forEach((cookieName) => {
    domains.forEach(({ domain, secure }) => {
      Cookies.remove(cookieName, { domain, path: '/', secure });
    });
    // Also try without domain to catch current domain
    Cookies.remove(cookieName, { path: '/' });
  });
};

export const AuthContext = React.createContext({
  user: null,
  login: null,
  logout: null,
});

export function AuthProvider(props) {
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Sentry.setUser(user);

    return () => Sentry.setUser(null);
  }, [user]);

  const fetchProfile = async () => {
    try {
      const profile = await authClient.getProfile();
      setUser(profile);
      analytics.identify(profile.id, {
        email: profile.email,
      });
    } catch (error) {
      setUser(null);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (getTokenFromStorage()) {
        try {
          await authClient.verifyAccount();
          await fetchProfile();
        } catch (error) {
          if (error.response?.status === 401) {
            setUser(null);
          }
        }
      }
      setInitialized(true);
    };

    const requestInterceptor = client.interceptors.request.use((conf) => {
      const token = getTokenFromStorage();
      const config = { ...conf };
      if (token) {
        config.headers.common.Authorization = `Bearer ${token}`;
      }
      return config;
    }, Promise.reject);

    init();

    return () => client.interceptors.request.eject(requestInterceptor);
  }, []);

  const login = async (email, password, staySignedIn, inviteToken) => {
    if (inviteToken) {
      await authClient.validateInviteAndlogin(email, password, inviteToken);
    } else {
      await authClient.login(email, password);
    }
    // Token is set via cookies by the auth service
    await fetchProfile();
    analytics.track('login');
  };

  const logout = () => {
    clearAuthCookies();
    clearTokenStorage();
    setUser(null);
    analytics.reset();
    navigate('/login');
  };

  const contextValue = useMemo(
    () => ({
      user,
      login,
      logout,
    }),
    [user],
  );

  return initialized ? (
    <AuthContext.Provider value={contextValue} {...props} />
  ) : (
    <Loading />
  );
}

export const useAuth = () => React.useContext(AuthContext);
